import axiosClient from './axiosClient';
import { createJsonAdapter } from './jsonAdapter';

export interface IStaffRoleRow {
  id: string;
  name: string;
  description?: string | null;
  features: string[];
  userCount: number;
}

export interface IStaffUserRow {
  id: string;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  staffRoleId: string;
  staffRoleName?: string | null;
}

const str = (value: unknown) => String(value ?? '').trim();
const list = (value: unknown) => str(value).split(',').filter(Boolean);

// Custom roles: a name and the admin pages they grant.
export const staffRoleAdapter = createJsonAdapter<IStaffRoleRow>({
  url: 'staff-roles',
  rowToForm: (row) => ({
    name: row.name ?? '',
    description: row.description ?? '',
    features: (row.features ?? []).join(','),
    userCount: row.userCount ?? 0,
  }),
  toBody: (form) => ({
    name: str(form.name),
    description: str(form.description),
    features: list(form.features),
  }),
});

// Staff accounts: the backend generates the password and emails it on create, so none is sent here;
// username and email are fixed once created (only the name and the role can change).
export const staffUserAdapter = createJsonAdapter<IStaffUserRow>({
  url: 'staff-users',
  rowToForm: (row) => ({
    username: row.username ?? '',
    email: row.email ?? '',
    firstName: row.firstName ?? '',
    lastName: row.lastName ?? '',
    staffRoleId: String(row.staffRoleId ?? ''),
    staffRoleName: row.staffRoleName ?? '',
  }),
  toBody: (form, isCreate) => ({
    ...(isCreate ? { username: str(form.username), email: str(form.email) } : {}),
    firstName: str(form.firstName),
    lastName: str(form.lastName),
    staffRoleId: Number(form.staffRoleId),
  }),
});

export const getStaffRoleOptions = async () => {
  const roles = await staffRoleAdapter.list({ page: 1, limit: 200 });
  return roles.rows.map((role) => ({ value: String(role.id), label: role.name }));
};

// Immediate (not a draft change): the backend sets a new random password and emails it to the user.
export const resetStaffPassword = (id: string) => axiosClient.post(`staff-users/${id}/reset-password`);
